import TagManager from "react-gtm-module";
import { service } from "../state_management/actionTypes";
import { ga_id } from "../settings";
import { currency, relu } from "./misc";
import { service_types } from "../state_management/reducers/cart";

declare global {
  interface Window {
    google_tag_manager: {
      [key: string]: { dataLayer?: { reset: () => void } };
    };
    oaiq?: (...args: any[]) => void;
  }
}

type payment_type = "Credit Card" | "Apple/Google Pay";

type gen_ga_type = {
  currency: currency;
  value: number;
};

type ga_item = {
  item_id: number;
  item_name: string;
  coupon?: string;
  price: number;
  quantity?: number;
  extra_service: boolean;
};

type ga_ecommerce = gen_ga_type & {
  items: ga_item[];
};

export type public_items = {
  id: number;
  quantity: number;
  type: service_types;
  item: {
    name: string;
    price_with_discount: number;
  };
}[];

export type ga_params_type = Omit<ga_ecommerce, "items"> & {
  items: public_items;
};

type CherryPick<T extends { items: any }> = Omit<T, "items"> &
  Pick<ga_ecommerce, "items">;

type event =
  | "add_to_cart"
  | "view_cart"
  | "remove_from_cart"
  | "begin_checkout"
  | "add_shipping_info"
  | "add_payment_info"
  | "purchase"
  | "view_item_list";

const isGTMAllowed = () => Boolean(window.tarteaucitron?.state?.ga_gtm);

const reset = () => {
  try {
    if (!isGTMAllowed()) return;

    if (window.google_tag_manager && window.google_tag_manager[ga_id]) {
      window.google_tag_manager[ga_id].dataLayer?.reset();
    }

    TagManager.dataLayer({
      dataLayer: undefined
    });
  } catch (err) {
    console.log(err);
  }
};

const callAnalytics = (dataParams: {
  event: event;
  params: object;
  user_data?: any;
}) => {
  if (!isGTMAllowed()) return;

  const params = { event: dataParams.event, ...dataParams.params };
  if (ga_id) {
    // Clear the previous ecommerce object
    reset();

    const dataLayer: any = {
      event: params.event,
      ecommerce: dataParams.params
    };
    
    if (dataParams.user_data) {
      dataLayer.user_data = dataParams.user_data;
    }

    TagManager.dataLayer({
      dataLayer: dataLayer
    });
  }
};

const convertItem = (
  items: public_items
): Pick<ga_ecommerce, "items">["items"] => {
  return items.map((item) => ({
    extra_service: item.type === "extra",
    item_id: item.id,
    item_name: item.item.name,
    price: item.item.price_with_discount,
    quantity: item.quantity
  }));
};

// done
export const add_cart_ga = (params: ga_params_type) => {
  const ga_item: ga_ecommerce = {
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "add_to_cart",
    params: ga_item
  });
};

// done
export const view_cart_ga = (params: ga_params_type) => {
  const ga_item: ga_ecommerce = {
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "view_cart",
    params: ga_item
  });
};

// done
export const remove_from_cart_ga = (params: ga_params_type) => {
  const ga_item: ga_ecommerce = {
    currency: params.currency,
    items: convertItem(params.items),
    value: params.value
  };

  callAnalytics({
    event: "remove_from_cart",
    params: ga_item
  });
};

// done
export const begin_checkout_ga = (params: ga_params_type) => {
  const ga_item: ga_ecommerce = {
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "begin_checkout",
    params: ga_item
  });
};

// done
export type add_shipping_info_params = ga_params_type & {
  coupon: string | null | undefined;
};
export const add_shipping_info_ga = (params: add_shipping_info_params) => {
  const ga_item: CherryPick<add_shipping_info_params> = {
    coupon: params.coupon,
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "add_shipping_info",
    params: ga_item
  });
};

// done
export type add_payment_info_params = ga_params_type & {
  coupon: string | null | undefined;
  payment_type?: payment_type | undefined;
};
export const add_payment_info_ga = (params: add_payment_info_params) => {
  const ga_item: CherryPick<add_payment_info_params> = {
    coupon: params.coupon,
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "add_payment_info",
    params: ga_item
  });
};

// done
type ga_view_list_type = {
  item_list_id?: number;
  extra: boolean;
  item_list_name: string;
  items: service[];
};
export const view_list_item_ga = (params: ga_view_list_type) => {
  const ga_item: Omit<ga_view_list_type, "items" | "extra"> & {
    items: ga_item[];
  } = {
    item_list_name: params.item_list_name,
    item_list_id: params.item_list_id,
    items: params.items.map((item) => ({
      extra_service: params.extra,
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      discount: relu(item.price - item.price_with_discount)
    }))
  };

  callAnalytics({
    event: "view_item_list",
    params: ga_item
  });
};

//
export type purchase_params = ga_params_type & {
  transaction_id?: number;
  coupon: string | null | undefined;
  payment_type?: payment_type | undefined;
  user_data?: {
    email_address?: string;
    phone_number?: string;
    address?: {
      first_name?: string;
      last_name?: string;
      city?: string;
      region?: string;
      postal_code?: string;
      country?: string;
    };
  };
};
export const purchase_ga = (params: purchase_params) => {
  const ga_item: CherryPick<purchase_params> = {
    transaction_id: params.transaction_id,
    coupon: params.coupon,
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "purchase",
    params: ga_item,
    user_data: params.user_data
  });
};

export const isOpenAIAllowed = () => Boolean(window.tarteaucitron?.state?.openai);

export const sha256 = async (str: string): Promise<string> => {
  try {
    const trimmed = str.trim().toLowerCase();
    if (/^[0-9a-f]{64}$/i.test(trimmed)) return trimmed;
    if (typeof window !== "undefined" && window.crypto?.subtle) {
      const buffer = new TextEncoder().encode(trimmed);
      const hashBuffer = await window.crypto.subtle.digest("SHA-256", buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch (e) {
    console.error("SHA-256 calculation error:", e);
  }
  return str.trim();
};

export const ensureOpprefCookie = (): string | null => {
  try {
    if (typeof window === "undefined") return null;
    const urlParams = new URLSearchParams(window.location.search);
    let oppref = urlParams.get("oppref");

    if (oppref) {
      sessionStorage.setItem("__oppref", oppref);
      localStorage.setItem("__oppref", oppref);
    } else {
      oppref =
        sessionStorage.getItem("__oppref") ||
        localStorage.getItem("__oppref");
    }

    if (oppref && document.cookie.indexOf("__oppref=") === -1) {
      const domainParts = window.location.hostname.split(".").filter(Boolean);
      const domain =
        domainParts.length >= 2
          ? `.${domainParts.slice(-2).join(".")}`
          : window.location.hostname;
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `__oppref=${encodeURIComponent(oppref)}; Path=/; Max-Age=2592000; SameSite=Lax; Domain=${domain}${secure}`;
      document.cookie = `__oppref=${encodeURIComponent(oppref)}; Path=/; Max-Age=2592000; SameSite=Lax${secure}`;
    }

    return oppref;
  } catch (err) {
    return null;
  }
};

export type openAiUserData = {
  external_id?: string | number;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
};

export type openAiOrderParams = {
  order_id: number | string;
  value: number | string;
  currency?: string;
  contents?: Array<{
    id?: string | number;
    name?: string;
    content_type?: string;
    quantity?: number;
    amount?: number;
    currency?: string;
  }>;
  user_data?: openAiUserData;
};

export const trackOpenAIOrderCreated = async ({
  order_id,
  value,
  currency = "EUR",
  contents,
  user_data
}: openAiOrderParams) => {
  try {
    if (typeof window.oaiq !== "function") return;
    if (!isOpenAIAllowed()) return;

    // Restore __oppref cookie if present in storage or URL
    ensureOpprefCookie();

    // Configure advanced matching user identifiers (e.g. external_id, email, phone, address)
    if (user_data) {
      const userConfig: Record<string, any> = {};

      const rawExternalId = user_data.external_id || user_data.email;
      if (rawExternalId) {
        userConfig.external_id_sha256 = await sha256(String(rawExternalId));
      }
      if (user_data.email) {
        userConfig.email_sha256 = await sha256(user_data.email);
      }
      if (user_data.phone) {
        userConfig.phone_number_sha256 = await sha256(user_data.phone);
      }
      if (user_data.first_name) {
        userConfig.first_name_sha256 = await sha256(user_data.first_name);
      }
      if (user_data.last_name) {
        userConfig.last_name_sha256 = await sha256(user_data.last_name);
      }
      if (user_data.city) userConfig.city = user_data.city;
      if (user_data.country) userConfig.country = user_data.country;
      if (user_data.postal_code) userConfig.postal_code = user_data.postal_code;
      if (user_data.region) userConfig.region = user_data.region;

      window.oaiq("init", {
        pixelId: "PuzMhRoCqnB75sUXTJfwFR",
        user: userConfig
      });
    }

    const amountInCents = Math.round((Number(value) || 0) * 100);

    const payload: Record<string, any> = {
      type: "contents",
      amount: amountInCents,
      currency: currency || "EUR"
    };

    if (Array.isArray(contents) && contents.length > 0) {
      payload.contents = contents;
    }

    window.oaiq(
      "measure",
      "order_created",
      payload,
      {
        event_id: String(order_id)
      }
    );
  } catch (err) {
    console.error("OpenAI pixel tracking error:", err);
  }
};
